'use client'

import { useRef } from 'react'
import { usePathname } from 'next/navigation'
import { ArrowRight, ArrowUp, Calendar, ChevronRight, Loader2, PackageOpen, Truck } from 'lucide-react'
import { useInfiniteProducts } from '@/hooks/use-infinite-products'
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll'
import { useCountdown } from '@/hooks/use-countdown'
import { useProductsByIds, useCategoryExpander } from '@/lib/commerce/hooks'
import { formatImageURLGoogle } from '@/utils/util'
import { getCampaignDiscountInfo, getCampaignTargetInfo, getCampaignStatus } from '@/utils/campaign'
import { navigateUrl } from '@/utils/client-request'
import { Skeleton } from '@/components/ui/skeleton'
import ProductCard from '../product/ProductCard'
import moment from 'moment'

const CAMPAIGN_PALETTE = ['#FFD9E7', '#E8E2FF', '#FFE0C9', '#D4F0DD', '#FFE9A8'] as const

function paletteFor(name?: string) {
  if (!name) return CAMPAIGN_PALETTE[0]
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  return CAMPAIGN_PALETTE[h % CAMPAIGN_PALETTE.length]
}

export default function Campaign({ vendor, campaign }: { vendor: any, campaign: any, wishlist?: any }) {
  const pathname = usePathname() || ''
  const targetInfo = getCampaignTargetInfo(campaign.targets)
  const discountInfo = getCampaignDiscountInfo(campaign.targets)
  const status = getCampaignStatus(campaign.startDate, campaign.endDate)
  const countdown = useCountdown(campaign.endDate, status === 'active')
  const heroBg = paletteFor(campaign.name)

  return (
    <section className="bg-cream pb-20 min-h-screen">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 pt-6">
        <nav className="text-[13px] text-foreground/55 mb-6 flex items-center gap-1.5 flex-wrap">
          <a href={navigateUrl('/', pathname)} className="hover:text-foreground">Нүүр</a>
          <ChevronRight className="size-3 opacity-50" />
          <a href={navigateUrl('/c', pathname)} className="hover:text-foreground">Хямдралын аян</a>
          <ChevronRight className="size-3 opacity-50" />
          <span className="text-foreground font-medium">{campaign.name}</span>
        </nav>
      </div>

      <div className="relative w-full overflow-hidden" style={{ background: heroBg }}>
        {campaign.campaignBanner ? (
          <div className="relative w-full aspect-16/7 sm:aspect-9/4 max-h-130 overflow-hidden">
            <img src={formatImageURLGoogle(campaign.campaignBanner)} alt={campaign.name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-linear-to-t from-black/40 to-transparent pointer-events-none" />
            <div className="absolute inset-x-0 bottom-0 mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 pb-8 sm:pb-12">
              <StatusBadge status={status} />
              <h1 className="mt-3 font-bold font-gilroy tracking-[-0.025em] leading-[0.95] text-white max-w-3xl drop-shadow-lg" style={{ fontSize: 'clamp(2.25rem, 6vw, 5rem)' }}>
                {campaign.name}.
              </h1>
              {discountInfo && (
                <div className="mt-3 text-[15px] sm:text-[17px] font-semibold text-white/95 drop-shadow">
                  {discountInfo.label} {discountInfo.suffix}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 py-20 sm:py-28 lg:py-32">
            <StatusBadge status={status} />
            <h1 className="mt-4 font-bold font-gilroy tracking-[-0.025em] leading-[0.95] text-foreground max-w-3xl" style={{ fontSize: 'clamp(2.5rem, 7vw, 6rem)' }}>
              {campaign.name}.
            </h1>
            {discountInfo && (
              <div className="mt-4 text-[18px] sm:text-[22px] font-semibold text-foreground/75">
                {discountInfo.label} {discountInfo.suffix}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 -mt-8 sm:-mt-10 relative z-10">
        <div className="bg-white rounded-3xl p-6 sm:p-7 flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-7">
          <div className="min-w-0 flex flex-col gap-2 lg:flex-1">
            <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-foreground/55">Хямдралын аян</div>
            {campaign.description ? (
              <p className="text-[14px] sm:text-[15px] text-foreground/70 leading-[1.55] max-w-2xl">{campaign.description}</p>
            ) : (
              <p className="text-[14px] text-foreground/55">Сонгогдсон бүтээгдэхүүнд урамшуулал идэвхтэй байна.</p>
            )}
          </div>

          {countdown && (
            <>
              <div className="hidden lg:block w-px h-14 bg-foreground/10" />
              <Countdown countdown={countdown} />
            </>
          )}

          {!countdown && campaign.startDate && (
            <>
              <div className="hidden lg:block w-px h-14 bg-foreground/10" />
              <div className="inline-flex items-center gap-2 text-[14px] text-foreground/65">
                <Calendar className="size-4" />
                <span className="font-medium tabular-nums">
                  {moment(campaign.startDate).format('MM.DD')} – {campaign.endDate ? moment(campaign.endDate).format('MM.DD') : '∞'}
                </span>
              </div>
            </>
          )}

          <div className="flex items-center gap-2 lg:ml-auto w-full sm:w-auto">
            {campaign.type === 'freeShipping' && (
              <span className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-emerald-50 text-emerald-700 text-[12.5px] font-semibold">
                <Truck className="size-3.5" /> Үнэгүй хүргэлт
              </span>
            )}
            <a href="#products" className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-foreground text-cream text-[13.5px] font-semibold hover:bg-foreground/90 transition-colors flex-1 sm:flex-initial">
              Бүгдийг харах <ArrowRight className="size-3.5" />
            </a>
          </div>
        </div>
      </div>

      <div id="products" className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 mt-14 sm:mt-16">
        {targetInfo.type === 'all' ? (
          <InfiniteCampaignGrid vendorId={vendor?.id} vendor={vendor} />
        ) : targetInfo.type === 'category' ? (
          <CategoryCampaignGrid vendorId={vendor?.id} categoryIds={targetInfo.categoryIds} vendor={vendor} />
        ) : targetInfo.type === 'product' ? (
          <ProductIdsCampaignGrid vendorId={vendor?.id} productIds={targetInfo.productIds} vendor={vendor} />
        ) : null}
      </div>
    </section>
  )
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center gap-2 h-7 px-3 rounded-full bg-emerald-500/95 text-white text-[11px] font-semibold uppercase tracking-[0.14em]">
        <span className="size-1.5 rounded-full bg-white animate-pulse" /> Идэвхтэй
      </span>
    )
  }
  if (status === 'expired') {
    return (
      <span className="inline-flex items-center gap-2 h-7 px-3 rounded-full bg-red-500/95 text-white text-[11px] font-semibold uppercase tracking-[0.14em]">Дууссан</span>
    )
  }
  return (
    <span className="inline-flex items-center gap-2 h-7 px-3 rounded-full bg-white/90 text-foreground text-[11px] font-semibold uppercase tracking-[0.14em]">Удахгүй</span>
  )
}

function pad2(n: number) { return String(n).padStart(2, '0') }

function Countdown({ countdown }: { countdown: { d: number, h: number, m: number, s: number } }) {
  const cells = [
    { v: countdown.d, k: 'өдөр' },
    { v: countdown.h, k: 'цаг' },
    { v: countdown.m, k: 'мин' },
    { v: countdown.s, k: 'сек' },
  ]
  return (
    <div className="inline-flex items-center gap-2 p-1.5 rounded-2xl bg-[#F1EEE7]">
      {cells.map(c => (
        <div key={c.k} className="flex flex-col items-center justify-center w-12 h-12 rounded-xl bg-white">
          <span className="text-[16px] font-bold font-gilroy tracking-[-0.02em] text-foreground leading-none tabular-nums">{pad2(c.v)}</span>
          <span className="text-[9px] text-foreground/55 mt-0.5 uppercase tracking-wider">{c.k}</span>
        </div>
      ))}
    </div>
  )
}

function CampaignProductGrid({
  products, isLoading, isLoadingMore, total, hasMore, loadMore, vendor, skeletonCount,
}: {
  products: any[], isLoading: boolean, isLoadingMore?: boolean, total: number, hasMore?: boolean, loadMore?: () => void, vendor: any, skeletonCount: number,
}) {
  const loadMoreRef = useRef<HTMLDivElement>(null)
  useInfiniteScroll({
    targetRef: loadMoreRef,
    onLoadMore: () => loadMore?.(),
    enabled: !!hasMore && !!loadMore && !isLoading && !isLoadingMore,
  })
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' })

  return (
    <div>
      <div className="flex items-end justify-between gap-4 mb-7">
        <div>
          <h2 className="font-bold font-gilroy tracking-[-0.025em] leading-none text-foreground" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)' }}>
            Хямдралын аянд хамрах бүтээгдэхүүн
          </h2>
          {!isLoading && (
            <div className="mt-2 text-[13.5px] text-foreground/55">{total} бүтээгдэхүүн</div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-5 gap-y-10">
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : products.length > 0 ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-5 gap-y-10">
            {products.map((product: any) => (
              <ProductCard key={product.customId || product.id} product={product} vendor={vendor} />
            ))}
          </div>
          {loadMore && (
            <div ref={loadMoreRef} className="w-full pt-12 flex justify-center">
              {isLoadingMore && (
                <div className="inline-flex items-center gap-2 text-foreground/55 text-[13px]">
                  <Loader2 className="size-4 animate-spin" /> Ачаалж байна...
                </div>
              )}
              {!hasMore && products.length > 0 && (
                <div className="flex flex-col items-center gap-4">
                  <span className="text-[13px] text-foreground/55">Бүх бүтээгдэхүүн ачаалагдлаа ({total})</span>
                  <button onClick={scrollToTop} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border-1.5 border-foreground/15 bg-transparent text-foreground text-[12.5px] font-semibold hover:border-foreground/30 transition-colors cursor-pointer">
                    <ArrowUp className="size-3.5" /> Дээш буцах
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="py-16 flex flex-col items-center text-center">
          <div className="w-20 h-20 rounded-3xl bg-[#F1EEE7] inline-flex items-center justify-center mb-5">
            <PackageOpen className="size-9 text-foreground/40" />
          </div>
          <h3 className="text-[20px] font-bold font-gilroy tracking-[-0.02em] text-foreground">Бүтээгдэхүүн байхгүй байна</h3>
          <p className="mt-2 text-[14px] text-foreground/55 max-w-sm">Энэ урамшуулалд хамаарах бүтээгдэхүүн одоогоор алга байна.</p>
        </div>
      )}
    </div>
  )
}

function InfiniteCampaignGrid({ vendorId, vendor }: { vendorId: string, vendor: any }) {
  const { products, isLoading, isLoadingMore, metadata, loadMore, hasMore } = useInfiniteProducts({ vendorId, limit: 20 })
  return (
    <CampaignProductGrid products={products} isLoading={isLoading} isLoadingMore={isLoadingMore} total={metadata.total} hasMore={hasMore} loadMore={loadMore} vendor={vendor} skeletonCount={10} />
  )
}

function CategoryCampaignGrid({ vendorId, categoryIds, vendor }: { vendorId: string, categoryIds: string[], vendor: any }) {
  const { expandedIds, loaded } = useCategoryExpander(vendorId, categoryIds)
  if (!loaded) {
    return (
      <div>
        <h2 className="font-bold font-gilroy tracking-[-0.025em] leading-none text-foreground mb-7" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)' }}>
          Хямдралын аянд хамрах бүтээгдэхүүн
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-5 gap-y-10">
          {Array.from({ length: 10 }).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      </div>
    )
  }
  return <CategoryCampaignGridLoaded vendorId={vendorId} expandedIds={expandedIds} vendor={vendor} />
}

function CategoryCampaignGridLoaded({ vendorId, expandedIds, vendor }: { vendorId: string, expandedIds: string, vendor: any }) {
  const { products, isLoading, isLoadingMore, metadata, loadMore, hasMore } = useInfiniteProducts({ vendorId, limit: 20, initialFilters: { category: expandedIds } })
  return (
    <CampaignProductGrid products={products} isLoading={isLoading} isLoadingMore={isLoadingMore} total={metadata.total} hasMore={hasMore} loadMore={loadMore} vendor={vendor} skeletonCount={10} />
  )
}

function ProductIdsCampaignGrid({ vendorId, productIds, vendor }: { vendorId: string, productIds: string[], vendor: any }) {
  const { products, isLoading } = useProductsByIds(vendorId, productIds)
  return (
    <CampaignProductGrid products={products} isLoading={isLoading} total={products.length} vendor={vendor} skeletonCount={productIds.length} />
  )
}

function SkeletonCard() {
  return (
    <div>
      <Skeleton className="aspect-square w-full rounded-2xl" />
      <Skeleton className="h-4 w-3/4 mt-4" />
      <Skeleton className="h-5 w-1/3 mt-2" />
    </div>
  )
}
